import { Trans, useTranslation } from "react-i18next";

import { blackOmgyes } from "@components/common/omgyesLogo";

import GiftPageFooter from "@pages/gift/components/GiftPageFooter";

interface GiftConfirmationProps {
  deliveryType: "send" | "print";
  recipientEmail?: string;
  purchaserEmail?: string;
  deliveryDate?: string;
}

const GiftConfirmation = ({
  deliveryType,
  recipientEmail,
  purchaserEmail,
  deliveryDate,
}: GiftConfirmationProps) => {
  const { t, i18n } = useTranslation();

  // Shared <Trans> component map: lets the WTI copy bold the recipient email
  // and "YES" via <bold> tags (OMG-2002).
  const boldComponents = { bold: <span className="font-mabry-pro-bold" /> };

  // Localize the scheduled-delivery date to the active language instead of
  // hardcoding en-US (OMG-2054). i18n codes like "nl_NL" → BCP-47 "nl-NL".
  const formattedDate = deliveryDate
    ? new Date(deliveryDate).toLocaleDateString(
        i18n.language?.replace(/_/g, "-") || "en-US",
        { month: "numeric", day: "numeric", year: "numeric" },
      )
    : undefined;

  // Only treat as "scheduled" when the delivery date is a future calendar day
  // in the buyer's local timezone. A gift sent today is also deliveryType
  // "send" with a date (today @ 9am), so the old truthy-date check wrongly
  // showed the scheduled-confirmation copy for same-day sends (OMG-1939).
  const startOfLocalDay = (d: Date) => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
  };
  const isScheduled =
    deliveryType === "send" &&
    !!deliveryDate &&
    startOfLocalDay(new Date(deliveryDate)) > startOfLocalDay(new Date());
  const isPrint = deliveryType === "print";

  // Set per stage by stacks/StaticSite.ts (and vite/.env locally), the same
  // way PageHeader and Menu read it. No fallback on purpose: the one that
  // used to be here hid a misnamed variable for months (OMG-2660).
  const siteUrl = import.meta.env.VITE_OMGYES_MARKETING_SITE_URL;

  return (
    <div className="flex flex-col items-center min-h-screen bg-white relative">
      {/* Background — zoomed image with white fade overlay. Capped to a fixed
          600px-tall band at the top of the page (matching the comp's `bg_header`,
          Figma node 3827-3219) so the orange fades to white within the hero and
          doesn't tint the whole page on tall/empty layouts (OMG-2001). Was
          `inset-0`, which stretched the gradient down the entire page. */}
      <div className="absolute top-0 left-0 right-0 h-[600px] pointer-events-none overflow-hidden">
        <img
          alt=""
          src="/members/assets/gift-confirmation-bg.jpg"
          className="absolute left-0 w-full"
          style={{ height: "233%", top: "-15%" }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(5deg, rgb(255,255,255) 28%, rgba(255,255,255,0) 98%)",
          }}
        />
      </div>

      {/* Header */}
      <div className="relative flex flex-row justify-between w-full max-w-[1040px] min-h-[68px] items-center px-[18px]">
        <a href={siteUrl} className="h-20px hover:cursor-pointer">
          {blackOmgyes}
        </a>
      </div>

      {/* Content */}
      <div className="relative flex flex-col items-center gap-[48px] px-[24px] mt-[80px] w-full max-w-[664px] text-center">
        <div className="flex flex-col gap-[24px] items-center">
          <h1 className="font-mabry-pro-bold text-[32px] leading-[36px] tracking-[-0.48px] text-ui-pepper">
            {t("giftConfirmation.heading")}
          </h1>

          <div className="text-[18px] leading-[24px] text-ui-pepper font-mabry-pro-regular">
            {/* Recipient email and "YES" render bold within the body copy
                (OMG-2002). Switched from plain t() to <Trans> so the WTI copy's
                <bold> tags resolve to a styled span; {{email}}/{{date}} still
                interpolate via `values`. */}
            {isPrint ? (
              // Match the scheduled/direct branches' paragraph spacing so the
              // print body doesn't butt against the thank-you line (OMG-1939).
              <p className="mb-[18px]">
                <Trans
                  i18nKey="giftConfirmation.print.body"
                  values={{ email: purchaserEmail }}
                  components={boldComponents}
                />
              </p>
            ) : isScheduled ? (
              <>
                <p className="mb-[18px]">
                  <Trans
                    i18nKey="giftConfirmation.scheduled.body"
                    values={{ date: formattedDate, email: recipientEmail }}
                    components={boldComponents}
                  />
                </p>
                <p className="mb-[18px]">
                  <Trans
                    i18nKey="giftConfirmation.scheduled.accessNote"
                    components={boldComponents}
                  />
                </p>
              </>
            ) : (
              <>
                <p className="mb-[18px]">
                  <Trans
                    i18nKey="giftConfirmation.direct.body"
                    values={{ email: recipientEmail }}
                    components={boldComponents}
                  />
                </p>
                <p className="mb-[18px]">
                  <Trans
                    i18nKey="giftConfirmation.direct.accessNote"
                    components={boldComponents}
                  />
                </p>
              </>
            )}

            <p>{t("giftConfirmation.thankYou")}</p>
          </div>
        </div>

        <a
          href={siteUrl}
          className="border border-cta text-cta font-mabry-pro-medium text-[16px] px-[25px] py-[8px] rounded-full hover:bg-cta hover:text-white transition-colors max-w-[312px] w-full inline-block text-center"
        >
          {t("giftConfirmation.ctaButton")}
        </a>
      </div>

      {/* Footer */}
      <div className="mt-auto w-full">
        <GiftPageFooter />
      </div>
    </div>
  );
};

export default GiftConfirmation;
